/**
 * Unified API Configuration for HealthHub Research Platform
 * Provides centralized configuration for all API endpoints and data types
 */

import {
  API_ENDPOINTS,
  buildApiUrl,
  apiClient,
  type ApiClientOptions,
} from "./api-client";
import type {
  ApiResponse,
  GlucoseReading,
  InsulinMeasurement,
  ECGFeatures,
  MealImage,
  ExtendedPatientSummary,
  D1NAMOPatientProfile,
} from "../types/api";
import type {
  GlucoseResponse,
  InsulinMeasurementResponse,
  ECGFeaturesResponse,
  MealImageResponse,
  ComprehensivePatientData,
} from "../data/types";
import {
  transformInsulinResponse,
  transformECGResponse,
  transformMealImagesResponse,
} from "../utils/data-transformers";

// ============================================================================
// Authentication Headers
// ============================================================================

export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ============================================================================
// API Request Builders
// ============================================================================

/**
 * Builds API URL with proper error handling
 */
export function buildApiEndpoint(
  endpoint: string,
  params?: Record<string, string | number>,
): string {
  const url = buildApiUrl(endpoint);
  if (!params) return url;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Generic API request with authentication
 */
export async function authenticatedRequest<TResponse>(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(getAuthHeaders());
  if (options.headers) {
    const extraHeaders = new Headers(options.headers as HeadersInit);
    extraHeaders.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  const method = options.method
    ? (options.method.toUpperCase() as ApiClientOptions["method"])
    : undefined;

  // Check if endpoint is already a full URL
  const isFullUrl =
    endpoint.startsWith("http://") || endpoint.startsWith("https://");

  if (isFullUrl) {
    // If it's already a full URL, use fetch directly
    return fetch(endpoint, {
      method,
      headers: Object.fromEntries(headers.entries()),
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: options.signal ?? undefined,
    });
  } else {
    // If it's a relative path, use apiClient but return a Response-like object
    const apiOptions: ApiClientOptions = {
      method,
      headers: Object.fromEntries(headers.entries()),
      body: options.body,
      signal: options.signal ?? undefined,
    };

    try {
      const data = await apiClient<TResponse>(endpoint, apiOptions);
      // Create a Response-like object for consistency
      return new Response(JSON.stringify(data), {
        status: 200,
        statusText: "OK",
        headers: { "Content-Type": "application/json" },
      }) as Response;
    } catch (error) {
      // Re-throw the error to be handled by the caller
      throw error;
    }
  }
}

// async function safeParseBody(response: Response): Promise<any | null> {
//   try {
//     return await response.json();
//   } catch {
//     return null;
//   }
// }

// ============================================================================
// D1NAMO Data API Functions
// ============================================================================

/**
 * Fetch glucose readings for a patient
 */
export async function fetchGlucoseReadings(
  patientId: string,
  options?: {
    startDate?: string;
    endDate?: string;
    readingType?: string;
  },
): Promise<GlucoseResponse> {
  const params = {
    patientId,
    ...(options?.startDate && { startDate: options.startDate }),
    ...(options?.endDate && { endDate: options.endDate }),
    ...(options?.readingType && { readingType: options.readingType }),
  };

  const response = await authenticatedRequest<any>(
    buildApiEndpoint(`${API_ENDPOINTS.GLUCOSE}`, params),
  );

  // Parse the JSON response and transform to expected format
  const apiResponse = await response.json();

  // Transform the response to match the expected format
  return {
    success: apiResponse.success || true,
    data: apiResponse.data || apiResponse.glucoseReadings || [],
    metadata: {
      count:
        apiResponse.data?.length || apiResponse.glucoseReadings?.length || 0,
      patientId,
      timeframe: {
        start: options?.startDate,
        end: options?.endDate,
      },
    },
    error: apiResponse.error,
  };
}

/**
 * Fetch insulin measurements for a patient
 */
export async function fetchInsulinMeasurements(
  patientId: string,
  options?: {
    startDate?: string;
    endDate?: string;
    type?: string;
  },
): Promise<InsulinMeasurementResponse> {
  const params = {
    patientId,
    ...(options?.startDate && { startDate: options.startDate }),
    ...(options?.endDate && { endDate: options.endDate }),
    ...(options?.type && { type: options.type }),
  };

  const response = await authenticatedRequest<any>(
    buildApiEndpoint(`${API_ENDPOINTS.INSULIN}`, params),
  );

  // Parse the JSON response and transform to expected format
  const apiResponse = await response.json();
  return transformInsulinResponse(apiResponse);
}

/**
 * Fetch ECG features for a patient
 */
export async function fetchECGFeatures(
  patientId: string,
  options?: {
    startDate?: string;
    endDate?: string;
    rhythmClassification?: string;
  },
): Promise<ECGFeaturesResponse> {
  const params = {
    patientId,
    ...(options?.startDate && { startDate: options.startDate }),
    ...(options?.endDate && { endDate: options.endDate }),
    ...(options?.rhythmClassification && {
      rhythmClassification: options.rhythmClassification,
    }),
  };

  const response = await authenticatedRequest<any>(
    buildApiEndpoint(`${API_ENDPOINTS.ECG}`, params),
  );

  // Parse the JSON response and transform to expected format
  const apiResponse = await response.json();
  return transformECGResponse(apiResponse);
}

/**
 * Fetch meal images for a patient
 */
export async function fetchMealImages(
  patientId: string,
  options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  },
): Promise<MealImageResponse> {
  const params = {
    patientId,
    ...(options?.startDate && { startDate: options.startDate }),
    ...(options?.endDate && { endDate: options.endDate }),
    ...(options?.limit && { limit: options.limit.toString() }),
  };

  const response = await authenticatedRequest<any>(
    buildApiEndpoint(`${API_ENDPOINTS.MEAL_IMAGES}`, params),
  );

  // Parse the JSON response and transform to expected format
  const apiResponse = await response.json();
  return transformMealImagesResponse(apiResponse);
}

/**
 * Fetch comprehensive patient data including all D1NAMO data types
 */
export async function fetchComprehensivePatientData(
  patientId: string,
  options?: {
    includeGlucose?: boolean;
    includeInsulin?: boolean;
    includeECG?: boolean;
    includeMealImages?: boolean;
    startDate?: string;
    endDate?: string;
  },
): Promise<ComprehensivePatientData> {
  const params = {
    patientId,
    includeGlucose: options?.includeGlucose?.toString() ?? "true",
    includeInsulin: options?.includeInsulin?.toString() ?? "true",
    includeECG: options?.includeECG?.toString() ?? "true",
    includeMealImages: options?.includeMealImages?.toString() ?? "true",
    ...(options?.startDate && { startDate: options.startDate }),
    ...(options?.endDate && { endDate: options.endDate }),
  };

  return authenticatedRequest<ComprehensivePatientData>(
    buildApiEndpoint(`${API_ENDPOINTS.PATIENT_PROFILES}/comprehensive`, params),
  );
}

/**
 * Fetch extended patient summary with D1NAMO data counts
 */
export async function fetchExtendedPatientSummary(
  patientId: string,
): Promise<ApiResponse & { patient: ExtendedPatientSummary }> {
  return authenticatedRequest(
    buildApiEndpoint(`${API_ENDPOINTS.PATIENTS}/${patientId}/extended`),
  );
}

/**
 * Upload meal image with base64 encoding
 */
export async function uploadMealImage(
  patientId: string,
  imageFile: File,
  metadata?: {
    mealId?: string;
    description?: string;
  },
): Promise<ApiResponse & { mealImage: MealImage }> {
  // Convert file to base64
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(imageFile);
  });

  const payload = {
    patientId,
    imageBase64: base64,
    metadata: {
      format: imageFile.type,
      size: imageFile.size,
      description: metadata?.description,
      mealId: metadata?.mealId,
    },
  };

  return apiClient(buildApiEndpoint(`${API_ENDPOINTS.MEAL_IMAGES}`), {
    method: "POST",
    body: payload,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
  });
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Enhanced error handling for D1NAMO data requests
 */
export class D1NAMOApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public endpoint?: string,
    public patientId?: string,
  ) {
    super(message);
    this.name = "D1NAMOApiError";
  }
}

/**
 * Wrapper for API calls with consistent error handling
 */
export async function withD1NAMOErrorHandling<TResponse>(
  apiCall: () => Promise<TResponse>,
  context: {
    endpoint: string;
    patientId?: string;
    operation: string;
  },
): Promise<TResponse> {
  try {
    return await apiCall();
  } catch (error) {
    if (error instanceof Error) {
      throw new D1NAMOApiError(
        `Failed to ${context.operation}: ${error.message}`,
        error.message.includes("404")
          ? 404
          : error.message.includes("401")
            ? 401
            : error.message.includes("403")
              ? 403
              : 500,
        context.endpoint,
        context.patientId,
      );
    }
    throw new D1NAMOApiError(
      `Unknown error during ${context.operation}`,
      500,
      context.endpoint,
      context.patientId,
    );
  }
}

// ============================================================================
// Export All Configuration
// ============================================================================

export {
  API_ENDPOINTS,
  buildApiUrl,
  apiClient,
  type ApiResponse,
  type GlucoseReading,
  type InsulinMeasurement,
  type ECGFeatures,
  type MealImage,
  type ExtendedPatientSummary,
  type D1NAMOPatientProfile,
  type GlucoseResponse,
  type InsulinMeasurementResponse,
  type ECGFeaturesResponse,
  type MealImageResponse,
  type ComprehensivePatientData,
};
